import { useNavigate, useLocation } from 'react-router-dom';
import UploadFiles from '../files/UploadFiles';
import { FileObject } from 'react-mui-dropzone';
import { OrderFileType } from '../files/order-file-type.type';
import { Icons } from '../common/ui-component';
import { useTranslation } from 'react-i18next';
import TakePicture from './TakePicture';
import { useEffect, useState } from 'react';
import { uploadHairLossPhotosHash } from './constants';
import { Page } from './ui';
import * as amplitude from '@amplitude/analytics-browser';
import { CustomProperties } from '../events/analytics-event.dto';

type Props = {
  handleUploadPhotos: (file: FileObject[], type: string) => void;
  handleDeletePhoto: (file: FileObject, index: number, type: string) => void;
  otherPhotos: FileObject[];
  handleTakePictureClick: (image: string, type: OrderFileType, onClose: () => void) => Promise<void>;
  disableTakePictureButton: boolean;
  setCameraDetectedAndAllowed: (value: boolean) => void;
  sendAnalyticsEvent: (eventName: string, eventProperties: CustomProperties, userProperties: CustomProperties) => void;
};

const LicensePhotoUpload = ({
  handleUploadPhotos,
  handleDeletePhoto,
  handleTakePictureClick,
  otherPhotos,
  disableTakePictureButton,
  setCameraDetectedAndAllowed,
  sendAnalyticsEvent,
}: Props) => {
  const { t } = useTranslation('photosPage');
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const [isTakePictureModeActivated, setIsTakePictureModeActivated] = useState(false);

  useEffect(() => {
    if (!otherPhotos.length) {
      navigate(`${pathname}${search}${uploadHairLossPhotosHash}`);
    }
    amplitude.track('photo_flow_id');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reviewLicensePhotoHash = '#review-license';

  const onPhotoUpload = (file: FileObject[]) => {
    handleUploadPhotos(file, 'license');
    sendAnalyticsEvent('Selected ID Photo', { upload_method: 'fileupload', CTA_name: 'Photo ID Selected' }, {});
    navigate(`${pathname}${search}${reviewLicensePhotoHash}`);
  };

  const onTakePictureSendClick = async (image: string, onClose: () => void) => {
    await handleTakePictureClick(image, 'license', onClose);
    sendAnalyticsEvent('Selected ID Photo', { upload_method: 'capture', CTA_name: 'Photo ID Selected' }, {});
    setIsTakePictureModeActivated(false);
    navigate(`${pathname}${search}${reviewLicensePhotoHash}`);
  };

  const onTakePictureClick = () => {
    sendAnalyticsEvent('Clicked CTA Take ID photo', { CTA_name: 'Take ID photo' }, {});
    setIsTakePictureModeActivated(true);
  };

  const handleOnUploadClick = () => {
    sendAnalyticsEvent('Clicked CTA Upload ID photo', { CTA_name: 'Upload ID photo' }, {});
  };

  return (
    <>
      <Page>
        <Page.Title>{t('lastStepUploadPhotoId')}</Page.Title>
        <Page.Text>{t('anImageOfYourPhotoIdIsRequired')}</Page.Text>
        <Page.UploadBox>
          <Page.UploadBox.Image>
            <Icons.License />
          </Page.UploadBox.Image>
          <Page.UploadBox.Container>
            <Page.UploadBox.Container.Title>{t('driversLicenseOrGovId')}</Page.UploadBox.Container.Title>
            <Page.UploadBox.Container.Text>{t('pleaseUploadLicense')}</Page.UploadBox.Container.Text>
            <Page.UploadBox.Container.Action>
              <Page.Button
                className="take-photo"
                disabled={disableTakePictureButton}
                onClick={() => onTakePictureClick()}
              >
                <Page.Button.Custom
                  className={`${disableTakePictureButton ? 'tw-bg-[#AAAAAA]' : 'tw-bg-secondary tw-border-secondary'}`}
                >
                  {t('takePhoto')}
                </Page.Button.Custom>
              </Page.Button>
              <UploadFiles
                onSuccess={(file) => onPhotoUpload(file)}
                onDelete={(file, index) => handleDeletePhoto(file, index, 'other')}
                files={otherPhotos}
                filesLimit={1}
                onUploadClick={() => handleOnUploadClick()}
              />
            </Page.UploadBox.Container.Action>
            {disableTakePictureButton && <Page.ErrorText>{t('noWebcamDetected')}</Page.ErrorText>}
          </Page.UploadBox.Container>
        </Page.UploadBox>
      </Page>
      {isTakePictureModeActivated && (
        <TakePicture
          onClose={() => setIsTakePictureModeActivated(false)}
          onTakePicture={(image: string) => onTakePictureSendClick(image, () => setIsTakePictureModeActivated(false))}
          setCameraDetectedAndAllowed={setCameraDetectedAndAllowed}
        />
      )}
    </>
  );
};

export default LicensePhotoUpload;
